import { useEffect, useRef, useState, useCallback } from "react";
import { UploadSimple, Sparkle, FileXls } from "@phosphor-icons/react";
import { toast } from "sonner";
import { debtorApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import DebtorsToolbar from "@/components/debtor/DebtorsToolbar";
import DebtorsTable from "@/components/debtor/DebtorsTable";
import PaymentModal from "@/components/debtor/PaymentModal";
import EditDebtorDialog from "@/components/debtor/EditDebtorDialog";

export default function DebtorsPage() {
  const [debtors, setDebtors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [q, setQ] = useState("");
  const [tipoFilter, setTipoFilter] = useState("todos");
  const [viewMode, setViewMode] = useState("activos"); // activos | pagados | todos
  const [payOpen, setPayOpen] = useState(false);
  const [payDebtor, setPayDebtor] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editDebtor, setEditDebtor] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setDebtors(await debtorApi.list());
    } catch {
      toast.error("Error cargando cartera");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleSeed = async () => {
    try {
      const r = await debtorApi.seedDemo();
      toast.success(r.message);
      load();
    } catch { toast.error("No se pudo cargar demo"); }
  };

  const resetFileInput = () => {
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) { resetFileInput(); return; }
    setImporting(true);
    try {
      const r = await debtorApi.uploadCsv(f);
      if (r.inserted === 0) {
        toast.warning("El archivo se leyó correctamente pero ninguna fila pudo ser registrada. Revise los datos o use la plantilla oficial.");
      } else {
        toast.success(`Base de datos importada correctamente: ${r.inserted} deudores añadidos`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error en importación");
    } finally {
      setImporting(false);
      resetFileInput();
    }
  };

  const handleCreate = async (payload) => {
    try {
      await debtorApi.create(payload);
      toast.success("Registro añadido");
      load();
      return true;
    } catch (err) {
      const detail = err.response?.data?.detail || "Error creando deudor";
      // 403 → límite alcanzado o cuenta suspendida (mensaje friendly listo desde el backend)
      if (err.response?.status === 403) {
        toast.error(detail, { duration: 8000 });
      } else {
        toast.error(detail);
      }
      return false;
    }
  };

  const handleDelete = async (id, e) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm("¿Eliminar este deudor?")) return;
    try {
      await debtorApi.remove(id);
      toast.success("Deudor eliminado");
      load();
    } catch { toast.error("Error eliminando"); }
  };

  const handleClearAll = async (password) => {
    const r = await debtorApi.clearAll(password);
    toast.success(`Eliminados ${r.deleted_debtors} deudores y ${r.deleted_receipts} comprobantes`);
    load();
  };

  const downloadTemplate = () => {
    const a = document.createElement("a");
    a.href = debtorApi.templateUrl;
    a.download = "plantilla_recobro.xlsx";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("Plantilla descargada con éxito");
  };

  const openAbono = (debtor) => {
    setPayDebtor(debtor);
    setPayOpen(true);
  };

  const openEdit = (debtor) => {
    setEditDebtor(debtor);
    setEditOpen(true);
  };

  const filtered = debtors.filter((d) => {
    const matchesQuery = [d.cliente_nombre, d.email, d.telefono].some((v) =>
      (v || "").toLowerCase().includes(q.toLowerCase())
    );
    if (!matchesQuery) return false;

    // Modo de vista: activos ocultan Pagado; pagados sólo Pagado (o saldo=0); todos = todos
    const saldo = Number(d.saldo_pendiente || 0);
    const esPagado = d.status === "Pagado" || saldo <= 0.01;
    if (viewMode === "activos" && esPagado) return false;
    if (viewMode === "pagados" && !esPagado) return false;

    if (tipoFilter === "todos") return true;
    const tipo = d.tipo_registro || "cartera_vencida";
    return tipo === tipoFilter;
  });

  const activosCount = debtors.filter((d) => !(d.status === "Pagado" || Number(d.saldo_pendiente || 0) <= 0.01)).length;
  const pagadosCount = debtors.length - activosCount;

  const showWelcome = !loading && debtors.length === 0 && !q;

  return (
    <div className="space-y-6" data-testid="debtors-page">
      <DebtorsToolbar
        query={q}
        onQuery={setQ}
        tipoFilter={tipoFilter}
        onTipoFilter={setTipoFilter}
        onUpload={handleFile}
        importing={importing}
        onSeed={handleSeed}
        onClearAll={handleClearAll}
        onDownloadTemplate={downloadTemplate}
        onCreate={handleCreate}
        fileRef={fileRef}
      />

      {/* View mode: Activos / Pagados / Todos */}
      {debtors.length > 0 && (
        <div className="inline-flex rounded-md border border-border p-0.5 bg-card" data-testid="view-mode-tabs">
          {[
            { key: "activos", label: "Cartera activa", count: activosCount },
            { key: "pagados", label: "Histórico pagados", count: pagadosCount },
            { key: "todos", label: "Todos", count: debtors.length },
          ].map((m) => (
            <button
              key={m.key}
              onClick={() => setViewMode(m.key)}
              data-testid={`view-mode-${m.key}`}
              className={`px-3 py-1.5 text-sm rounded-sm transition-all ${
                viewMode === m.key
                  ? "bg-primary text-primary-foreground font-medium"
                  : "text-foreground/70 hover:bg-accent"
              }`}
            >
              {m.label}
              <span className={`ml-1.5 text-[10px] font-mono ${viewMode === m.key ? "opacity-70" : "text-muted-foreground"}`}>
                {m.count}
              </span>
            </button>
          ))}
        </div>
      )}

      {showWelcome ? (
        <div className="bg-card border border-border rounded-md p-12 text-center" data-testid="empty-state-welcome">
          <div className="w-16 h-16 rounded-md bg-primary/10 grid place-items-center mx-auto mb-5 text-primary">
            <UploadSimple size={30} weight="duotone" />
          </div>
          <h2 className="font-heading text-2xl font-semibold tracking-tight">
            Bienvenido a Recobro.AI
          </h2>
          <p className="text-muted-foreground mt-2 max-w-md mx-auto">
            Aún no tienes deudores. Carga tu primer Excel para empezar y los agentes de IA clasificarán automáticamente el riesgo.
          </p>
          <div className="mt-6 flex gap-2 justify-center flex-wrap">
            <Button onClick={() => fileRef.current?.click()} size="lg" data-testid="empty-import-btn">
              <UploadSimple size={18} weight="bold" className="mr-2" /> Cargar mi primer Excel
            </Button>
            <Button variant="outline" onClick={downloadTemplate} size="lg">
              <FileXls size={18} weight="duotone" className="mr-2" /> Descargar plantilla
            </Button>
            <Button variant="outline" onClick={handleSeed} size="lg" data-testid="empty-demo-btn">
              <Sparkle size={18} weight="duotone" className="mr-2" /> Probar con datos demo
            </Button>
          </div>
        </div>
      ) : (
        <DebtorsTable
          debtors={filtered}
          loading={loading}
          totalCount={debtors.length}
          onDelete={handleDelete}
          onAbono={openAbono}
          onEdit={openEdit}
        />
      )}

      <PaymentModal
        open={payOpen}
        onOpenChange={setPayOpen}
        debtor={payDebtor}
        onDone={() => load()}
      />

      <EditDebtorDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        debtor={editDebtor}
        onSaved={() => load()}
      />
    </div>
  );
}
